import { MIN_PIECE_SEC } from './clipSpanOverlap';
import { segmentStartMs } from './dubWordTimeline';

import type { DubVoiceMode } from './DubEngine';
import type { ClipSpan } from './DubPlacementService';
import type { OriginalAudioPiece, OriginalLevelRange } from './segmentLevels';
import type { DubManifestDTO } from '@src/repositories/DubbingRepository';

/**
 * The original lead-in, on Both: the speaker is heard first, in their own
 * voice, and the dub comes in on its line. The first seconds of the speaker's
 * separated voice — from the first dubbed line on — are moved earlier, into the
 * room the clip has before that line, so they end where the dub begins
 * (`planLeadInVoice`). Only the voice moves: the picture's own sound and the
 * music stay where they are. Where the room is short the voice starts with the
 * picture and runs on under the dub, which the dub's own delay can clear.
 *
 * Before the story is separated there is no voice on its own, so the
 * recording's own sound is moved instead, room and music with it, and the
 * original is silent under it (`planDubComposition`).
 *
 * Pure: no MobX, no React, no IO.
 *
 * The dub's delay is applied once, to the dub's description, at the gate that
 * says which dub is heard (`resolveHeardDub`). Everything downstream — the
 * voice elements, the dub-timed captions, the review's playhead — reads the dub
 * from there, so the voice and the words shown for it cannot come apart.
 *
 * Off is no key at all, so a clip that does not use it keeps the stored shape it
 * had before this existed (the publish staleness hash, see
 * `dubOptionsAreEmpty`).
 */

export interface DubLeadIn {
  /** How long the speaker's own voice plays before the dub, in seconds. */
  originalPlaysSec: number;
  /**
   * How long after its line each dub comes in, in seconds — to clear a lead-in
   * that runs on under the dub. Absent means on its line.
   */
  delaySec?: number;
  /**
   * The original fades out where it is muted under the dub, and back in where
   * the dub ends, instead of cutting; the lead-in voice fades out at its end.
   * Only into silence: a fade always ends at nothing, so a fade into a lowered
   * level would dip and jump back. How long each fade takes, in seconds;
   * absent means it cuts.
   */
  fadeSec?: number;
  /**
   * The lead-in's level, and the original's before the dub, 0–99. Absent means
   * full, and full is never written — the hash rule above.
   */
  leadInLevel?: number;
}

/** How long the speaker plays when the lead-in is turned on. */
export const DEFAULT_LEAD_IN_SEC = 2;

/** The longest lead-in or delay the control offers. */
export const MAX_LEAD_IN_SEC = 10;

/** How long the fade takes when it is turned on. */
export const DEFAULT_LEAD_IN_FADE_SEC = 1;

/** The longest fade the control offers. */
export const MAX_LEAD_IN_FADE_SEC = 5;

/** A stored length, held to 0–max in tenths; anything else is unset. */
function holdSeconds(raw: unknown): number | undefined {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined;
  return Math.round(Math.min(MAX_LEAD_IN_SEC, Math.max(0, raw)) * 10) / 10;
}

/**
 * A stored level as a whole 0–99, or unset for full or anything unreadable —
 * the rule `normalizeDubVoiceVolume` holds the clip's levels to.
 */
function holdLevelBelowFull(raw: unknown): number | undefined {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined;
  const level = Math.round(Math.min(100, Math.max(0, raw)));
  return level === 100 ? undefined : level;
}

/**
 * The lead-in as it is held, or `undefined` when it is off. No length is off,
 * and a delay of nothing is not written.
 *
 * A lead-in saved before the speaker's voice was moved holds only `delaySec`,
 * which then played the original for that long and held the dub back by it.
 * It reads as a lead-in of that length with the dub on its line.
 */
export function holdDubLeadIn(raw: unknown): DubLeadIn | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const held = raw as Partial<DubLeadIn>;
  const heldPlays = holdSeconds(held.originalPlaysSec);
  const originalPlaysSec = heldPlays ?? holdSeconds(held.delaySec);
  if (!originalPlaysSec) return undefined;
  const delaySec =
    heldPlays === undefined ? undefined : holdSeconds(held.delaySec);
  const leadInLevel = holdLevelBelowFull(held.leadInLevel);
  const heldFade = holdSeconds(held.fadeSec);
  const fadeSec =
    heldFade === undefined ? 0 : Math.min(MAX_LEAD_IN_FADE_SEC, heldFade);
  return {
    originalPlaysSec,
    ...(delaySec && { delaySec }),
    ...(fadeSec && { fadeSec }),
    ...(leadInLevel !== undefined && { leadInLevel }),
  };
}

/** Do two lead-ins hold the same? */
export function sameDubLeadIn(
  a: DubLeadIn | undefined,
  b: DubLeadIn | undefined,
): boolean {
  const heldA = holdDubLeadIn(a);
  const heldB = holdDubLeadIn(b);
  return (
    heldA?.originalPlaysSec === heldB?.originalPlaysSec &&
    heldA?.delaySec === heldB?.delaySec &&
    heldA?.fadeSec === heldB?.fadeSec &&
    heldA?.leadInLevel === heldB?.leadInLevel
  );
}

/** The original's level through the lead-in, from the clip's start, 0–100. */
export function leadInOriginalLevel(leadIn: DubLeadIn): number {
  return leadIn.leadInLevel ?? 100;
}

/** How long after its line each dub comes in. */
export function leadInDubDelaySec(leadIn: DubLeadIn): number {
  return leadIn.delaySec ?? 0;
}

/** The lead-in that is heard: on Both only, and only when it is on. */
export function heardLeadIn(
  voiceMode: DubVoiceMode | null | undefined,
  leadIn: DubLeadIn | undefined,
): DubLeadIn | undefined {
  return voiceMode === 'both' ? holdDubLeadIn(leadIn) : undefined;
}

/**
 * Delayed copies, by description and delay. The description held in memory is
 * one object until its next fetch, so its delayed copy is too: a reader that
 * compares descriptions by identity sees no change on an unrelated pass.
 */
const delayedManifests = new WeakMap<
  DubManifestDTO,
  Map<number, DubManifestDTO>
>();

/**
 * The description with every line laid `delaySec` later on the recording's
 * clock. Only where a line plays moves (`placedStartMs`); where it was spoken
 * (`originalStartMs`) stays, so a line is still matched to the words it
 * translates.
 */
export function delayDubManifest(
  manifest: DubManifestDTO,
  delaySec: number,
): DubManifestDTO {
  const delayMs = Math.round(delaySec * 1000);
  if (delayMs === 0) return manifest;
  let byDelay = delayedManifests.get(manifest);
  if (!byDelay) {
    byDelay = new Map();
    delayedManifests.set(manifest, byDelay);
  }
  const cached = byDelay.get(delayMs);
  if (cached) return cached;
  const delayed: DubManifestDTO = {
    ...manifest,
    segments: manifest.segments.map(segment => ({
      ...segment,
      placedStartMs: segmentStartMs(segment) + delayMs,
    })),
  };
  byDelay.set(delayMs, delayed);
  return delayed;
}

/** One voice element as the lead-in reads it: where it plays, and its line. */
interface LeadInVoiceElement {
  time: number;
  duration: number;
  segmentIds: readonly string[];
}

/**
 * Where the original is turned down, on the clip's clock. The original plays
 * at its lead-in level until the speaker starts — where the first dubbed line
 * was spoken, before any delay. It is then silent for the `heardSec` the
 * lead-in voice already played (`planLeadInVoice`), so under the dub it picks
 * up where the lead-in left off rather than saying those words again. From
 * there to `untilSec` it is at the clip's level under the dub — between lines
 * and at the start of every later line too. A line with its own Original
 * level is at that level while its dub plays, past the silent stretch.
 *
 * Each element carries one line (the placement keeps every line apart while a
 * lead-in is on), each the delay after its line's start. The ranges never
 * overlap, so a line's own level is never hidden under the clip's.
 */
export function planLeadInOriginalRanges(
  elements: readonly LeadInVoiceElement[],
  leadIn: DubLeadIn,
  clipLevelUnderDub: number,
  ownLevelUnderDub: (segmentIds: readonly string[]) => number | undefined,
  untilSec: number,
  heardSec = 0,
): OriginalLevelRange[] {
  if (elements.length === 0) return [];
  const startSec =
    Math.min(...elements.map(element => element.time)) -
    leadInDubDelaySec(leadIn);
  const ranges: OriginalLevelRange[] = [];
  let cursor = startSec;
  const turnDown = (endSec: number, level: number) => {
    if (endSec > cursor) ranges.push({ startSec: cursor, endSec, level });
    cursor = Math.max(cursor, endSec);
  };
  turnDown(Math.min(startSec + heardSec, untilSec), 0);
  for (const element of elements) {
    const own = ownLevelUnderDub(element.segmentIds);
    if (own === undefined) continue;
    const endSec = element.time + element.duration;
    turnDown(Math.max(element.time, cursor), clipLevelUnderDub);
    turnDown(endSec, own);
  }
  turnDown(untilSec, clipLevelUnderDub);
  return ranges;
}

/** The lead-in voice, as one audio element's numbers on the clip's clock. */
export interface LeadInVoicePiece {
  time: number;
  duration: number;
  /** Where in the separated voice (or the recording) it starts, seconds. */
  trim_start: number;
  fadeOutSec?: number;
}

/**
 * The speaker's own voice, moved ahead of the dub: their first
 * `originalPlaysSec` of separated voice from where the first dubbed line was
 * spoken (`firstLineSec`, on the clip's clock, before any delay), laid to end
 * there — the dub comes in on its line just as the speaker hands over. Where
 * the clip has less room than that before the line, it starts with the
 * picture and runs on under the dub.
 *
 * It never runs past the kept stretch the line sits in, so it plays no words
 * the clip cut out. `null` when the line is outside every kept stretch.
 */
export function planLeadInVoice(
  firstLineSec: number,
  spans: readonly ClipSpan[],
  leadIn: DubLeadIn,
): LeadInVoicePiece | null {
  const span = spans.find(
    candidate =>
      candidate.clipStartSec <= firstLineSec &&
      firstLineSec < candidate.clipStartSec + candidate.durationSec,
  );
  if (!span) return null;
  const pictureStartSec = Math.min(...spans.map(each => each.clipStartSec));
  const duration = Math.min(
    leadIn.originalPlaysSec,
    span.clipStartSec + span.durationSec - firstLineSec,
  );
  if (duration < MIN_PIECE_SEC) return null;
  const fadeOutSec = leadIn.fadeSec
    ? Math.min(leadIn.fadeSec, duration / 2)
    : undefined;
  return {
    time: Math.max(pictureStartSec, firstLineSec - duration),
    duration,
    trim_start: span.sourceStartSec + (firstLineSec - span.clipStartSec),
    ...(fadeOutSec !== undefined && { fadeOutSec }),
  };
}

/** One piece of the original's sound, as the fades read it. */
interface FadedPiece {
  time: number;
  duration: number;
  /** 0–100. */
  level: number;
}

/**
 * The fades that take the original into the mute and out of it again: a
 * heard piece that runs straight into a silent one fades out at its end, and
 * one that follows a silent one fades in at its start. Each fade is at most
 * half its piece, so a short piece can fade both ways. The pieces are in
 * clip order; a piece with no fade gets none.
 */
export function planFadesIntoMute(
  pieces: readonly FadedPiece[],
  fadeSec: number,
): { fadeInSec?: number; fadeOutSec?: number }[] {
  const touches = (a: FadedPiece, b: FadedPiece | undefined) =>
    !!b && Math.abs(a.time + a.duration - b.time) < 0.001;
  return pieces.map((piece, i) => {
    if (piece.level === 0) return {};
    const pieceFadeSec = Math.min(fadeSec, piece.duration / 2);
    const next = pieces[i + 1];
    const previous = pieces[i - 1];
    return {
      ...(touches(piece, next) &&
        next.level === 0 && { fadeOutSec: pieceFadeSec }),
      ...(previous &&
        touches(previous, piece) &&
        previous.level === 0 && { fadeInSec: pieceFadeSec }),
    };
  });
}

/** One stretch of the music bed on Both, on the clip's clock. */
export interface BedPiece extends OriginalAudioPiece {
  fadeInSec?: number;
  fadeOutSec?: number;
}

/**
 * The music bed on Both, when the original is laid as pieces (a lead-in, or a
 * line at its own level): the story's music carries on wherever the original
 * is turned down. The bed and the original's own music are the same
 * recording split in two, so wherever the original plays at a level the bed
 * plays at what it is turned down by — the music stays whole while the
 * speaker's voice is at the original's level. Where the original is at full
 * (the lead-in itself) there is no bed at all.
 *
 * Where the original fades into a mute the bed fades in over the same seconds,
 * and out where the original fades back, so the music does not dip through the
 * fade. Only between pieces that run on in the recording too: across a seam
 * the music cuts with the picture.
 */
export function planBedUnderOriginal(
  pieces: readonly OriginalAudioPiece[],
  fades: readonly { fadeInSec?: number; fadeOutSec?: number }[],
): BedPiece[] {
  const bed: BedPiece[] = pieces.map(piece => ({
    ...piece,
    level: 100 - piece.level,
  }));
  const shift = (piece: BedPiece, sec: number) => {
    piece.time += sec;
    piece.trim_start += sec;
    piece.duration -= sec;
  };
  pieces.forEach((piece, i) => {
    const next = pieces[i + 1];
    if (
      !next ||
      Math.abs(piece.time + piece.duration - next.time) >= 0.001 ||
      Math.abs(piece.trim_start + piece.duration - next.trim_start) >= 0.001
    ) {
      return;
    }
    const fadeOutSec = fades[i]?.fadeOutSec;
    if (fadeOutSec) {
      bed[i].duration -= fadeOutSec;
      shift(bed[i + 1], -fadeOutSec);
      bed[i + 1].fadeInSec = fadeOutSec;
    }
    const fadeInSec = fades[i + 1]?.fadeInSec;
    if (fadeInSec) {
      bed[i].duration += fadeInSec;
      bed[i].fadeOutSec = fadeInSec;
      shift(bed[i + 1], fadeInSec);
    }
  });
  return bed.filter(
    piece => piece.level > 0 && piece.duration >= MIN_PIECE_SEC,
  );
}
